// ===========================================
// Error Boundary Component
// ===========================================
// Catches JavaScript errors in child components and displays a fallback UI

import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Collapse,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// ===========================================
// TYPES
// ===========================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showStack: boolean;
}

// ===========================================
// DEFAULT FALLBACK COMPONENT
// ===========================================

interface DefaultFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onRetry: () => void;
  showDetails?: boolean;
}

function DefaultFallback({ error, errorInfo, onRetry, showDetails = false }: DefaultFallbackProps) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          textAlign: 'center',
        }}
      >
        <ErrorIcon
          sx={{
            fontSize: 64,
            color: 'error.main',
            mb: 2,
          }}
        />

        <Typography variant="h5" gutterBottom color="error">
          Something went wrong
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          We're sorry, but an unexpected error occurred. Please try refreshing the page or
          returning to the home page.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            <AlertTitle>Error</AlertTitle>
            {error.message || 'An unexpected error occurred'}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => (window.location.href = '/')}
          >
            Go to Home
          </Button>
        </Box>

        {showDetails && errorInfo && (
          <Box sx={{ textAlign: 'left' }}>
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ mb: 1 }}
            >
              {expanded ? 'Hide' : 'Show'} Technical Details
            </Button>
            <Collapse in={expanded}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {errorInfo.componentStack}
                </Typography>
              </Paper>
            </Collapse>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// Need to import React for useState in DefaultFallback
import React from 'react';

// ===========================================
// ERROR BOUNDARY CLASS
// ===========================================

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showStack: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // ⚠️ NEVER gate this on the environment. A caught error is invisible by
    // construction — the user sees a tidy card, not a stack — so this is the
    // only signal that anything broke. It used to sit behind
    // `process.env.NODE_ENV === 'development'`, which is dead in BOTH
    // environments here: Vite strips it from the production bundle, and this
    // codebase uses `import.meta.env` everywhere else, so dev never set it
    // either. The result was a boundary that logged nowhere, ever.
    //
    // This matters more since 2026-09-15, when 93 admin routes gained
    // boundaries: pages that used to fail loudly now fail quietly. There is
    // still no error-reporting service wired (see PROGRESS.md Pre-Existing
    // Issues) — `onError` below is the hook for one.
    console.error('ErrorBoundary caught an error:', error);
    if (import.meta.env.DEV) {
      console.error('Component stack:', errorInfo.componentStack);
    }

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use default fallback
      return (
        <DefaultFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          // `import.meta.env.DEV`, not `process.env.NODE_ENV` — the latter is
          // undefined in this Vite app, so the component stack panel never
          // appeared for a developer either.
          showDetails={this.props.showDetails ?? import.meta.env.DEV}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
